import {
  FetchMoveOutDataSuccessResponse,
  HomeMovesCustomerData,
} from '@/src/api/home-moves/types';
import { HOME_MOVES_START } from '@/src/constants/analytics-subjects';
import { MovingDateValues } from '@/src/types/MoveOut';
import { Card, ErrorNotification, Heading1, P } from '@ovotech/element-v4';
import {
  useAccountContext,
  useAnalytics,
  useCustomerContext,
} from '@ovotech/ui-tools';
import React, { useEffect, useState } from 'react';
import { QueryClient, useQueryClient } from 'react-query';
import { toDateString } from '../../utils/movingDateValuesConversion';
import { redirectToQS } from '../../utils/redirectToQS';
import { scrollToFirstError } from '../../utils/validationUtils';
import {
  ConfirmModal,
  ConfirmModalVariant,
} from '../ConfirmModal/ConfirmModal';
import { GbgAddressSection } from '../GbgAddressSection/GbgAddressSection';
import { ManualAddressSection } from '../ManualAddressSection/ManualAddressSection';
import { MovingDateSection } from '../MovingDateSection/MovingDateSection';
import { Rewards } from '../Rewards';
import styles from './MovingHomeForm.module.css';
import { useMovingHomeApi } from './useMovingHomeApi';
import {
  FormSubmissionSource,
  useMovingHomeFormState,
} from './useMovingHomeFormState';
/*
 * TODO Revisit this type so we can ensure its strict. ConfirmModalVariant.NO_UK_ADDRESS should have no
 * address but a date and for both other variants there should be an address but no date
 */

type ConfirmModalState =
  | {
      isOpen: false;
    }
  | ({
      isOpen: true;
    } & { data: ConfirmModalData });

type ConfirmModalData =
  | {
      variant: ConfirmModalVariant.NO_UK_ADDRESS;
      movingDate: MovingDateValues;
    }
  | {
      variant: ConfirmModalVariant.QUOTES | ConfirmModalVariant.END_CONTRACT;
      address: string;
      movingDate?: MovingDateValues;
    };

const optimisticallyUpdateMoveOutDate = ({
  queryClient,
  accountId,
  movingDate,
}: {
  queryClient: QueryClient;
  accountId?: string | null;
  movingDate: MovingDateValues;
}) => {
  queryClient.setQueryData(
    ['home-moves', 'status', accountId],
    (
      oldData: FetchMoveOutDataSuccessResponse | undefined,
    ): FetchMoveOutDataSuccessResponse => {
      return {
        ...oldData,
        canSelfServe: true,
        hasSubmittedFinalReads: false,
        moveOutDate: toDateString(movingDate),
        supplyPointAddress: oldData?.supplyPointAddress ?? null,
      };
    },
  );
};

export const MovingHomeForm = () => {
  const { accountId } = useAccountContext();
  const { customerId } = useCustomerContext();
  const queryClient = useQueryClient();

  const { dispatch } = useAnalytics();

  // This will be set to true if the user uses a GBG COT address in the
  //  Eligibility component a couple of layers down the tree. Then this value
  // will be used when saving the customer move out details to the API.
  const [isCot, setIsCot] = useState(false);

  const {
    formRef,
    formValues,
    formErrors,
    addressProvider,
    setAddressProvider,
    formSubmissionState,
    setFormSubmissionState,
    validateForm,
    valuesChangeHandler,
    errorsChangeHandler,
  } = useMovingHomeFormState();

  const {
    submitMoveOutDetails,
    isLoading: isApiLoading,
    error: apiError,
    createMoveOut,
  } = useMovingHomeApi();

  const [confirmModalState, setConfirmModalState] = useState<ConfirmModalState>(
    {
      isOpen: false,
    },
  );

  const showConfirmationModal = (variant: ConfirmModalVariant) => {
    const { gbgAddress, manualAddress, movingDate } = formValues;

    if (formValues.gbgAddress.addressOutsideUk) {
      return setConfirmModalState({
        isOpen: true,
        data: {
          movingDate: movingDate,
          variant: ConfirmModalVariant.NO_UK_ADDRESS,
        },
      });
    }

    const address =
      addressProvider === 'manual'
        ? [
            manualAddress.houseNumberOrName,
            manualAddress.addressLine1,
            manualAddress.addressLine2,
            manualAddress.town,
            manualAddress.postcode,
          ]
            .filter(Boolean)
            .join(', ')
        : gbgAddress.address?.fullAddress || '';

    setConfirmModalState({
      isOpen: true,
      data: {
        address,
        variant,
        movingDate,
      },
    });
  };

  const closeConfirmModal = () => {
    setConfirmModalState({ isOpen: false });
  };

  const handleConfirmSubmission = async () => {
    if (!confirmModalState.isOpen) return;

    const modalVariant = confirmModalState.data.variant;

    if (modalVariant === ConfirmModalVariant.NO_UK_ADDRESS) {
      await createMoveOut({
        movingDate: formValues.movingDate,
      });

      optimisticallyUpdateMoveOutDate({
        queryClient,
        accountId,
        movingDate: formValues.movingDate,
      });

      closeConfirmModal();

      return;
    }

    const success = await submitMoveOutDetails({
      movingDate: formValues.movingDate,
      gbgAddress: formValues.gbgAddress,
      manualAddress: formValues.manualAddress,
      addressProvider: addressProvider,
      isCot,
    });

    if (modalVariant === ConfirmModalVariant.QUOTES && success) {
      redirectToQS(accountId, customerId);
    } else if (success) {
      optimisticallyUpdateMoveOutDate({
        queryClient,
        accountId,
        movingDate: formValues.movingDate,
      });

      queryClient.setQueryData(
        ['home-moves', 'customer', accountId],
        (): HomeMovesCustomerData => {
          const moveOutDate = toDateString(formValues.movingDate);

          const postcode =
            addressProvider === 'gbg'
              ? formValues.gbgAddress.postCode
              : formValues.manualAddress.postcode;

          return addressProvider === 'manual'
            ? {
                moveOutDate,
                postcode,
                manualAddress: {
                  ...formValues.manualAddress,
                  addressProvider: 'manual',
                },
              }
            : {
                moveOutDate,
                postcode,
                isCot,
                automaticAddress: {
                  addressProvider: 'gbgV2' as const,
                  postcode: formValues.gbgAddress.postCode,
                  address: formValues.gbgAddress.address?.fullAddress || '',
                  fullAddress: formValues.gbgAddress.address?.fullAddress || '',
                  propertySearchId: '',
                },
              };
        },
      );

      closeConfirmModal();
    }
  };

  function handleGbgAddressGetQuote({
    isCot,
    formSubmissionSource,
  }: {
    isCot: boolean;
    formSubmissionSource: FormSubmissionSource;
  }) {
    const isValid = validateForm(formValues);

    setFormSubmissionState({
      submitted: true,
      isValid,
      source: formSubmissionSource,
    });

    if (!isValid) {
      scrollToFirstError(formRef);
      return;
    }

    setIsCot(isCot);
    showConfirmationModal(ConfirmModalVariant.QUOTES);
  }

  const submitHandler = (formSubmissionSource: FormSubmissionSource) => {
    return () => {
      const isValid = validateForm(formValues);

      setFormSubmissionState({
        submitted: true,
        isValid,
        source: formSubmissionSource,
      });

      if (!isValid) {
        scrollToFirstError(formRef);
        return;
      }

      switch (formSubmissionSource) {
        case FormSubmissionSource.GBG_ADDRESS_END_CONTRACT: {
          showConfirmationModal(ConfirmModalVariant.END_CONTRACT);
          break;
        }

        case FormSubmissionSource.MANUAL_ADDRESS_NEXT: {
          showConfirmationModal(ConfirmModalVariant.END_CONTRACT);
          break;
        }

        case FormSubmissionSource.GBG_ADDRESS_GET_QUOTE: {
          showConfirmationModal(ConfirmModalVariant.QUOTES);
          break;
        }
        case FormSubmissionSource.NO_UK_ADDRESS_NEXT: {
          showConfirmationModal(ConfirmModalVariant.NO_UK_ADDRESS);
          break;
        }

        default:
          break;
      }
    };
  };

  useEffect(() => {
    dispatch({
      type: 'view',
      name: HOME_MOVES_START,
    });
  }, [dispatch]);

  return (
    <>
      <Card>
        {confirmModalState.isOpen && (
          <ConfirmModal
            {...(confirmModalState.data.variant ===
            ConfirmModalVariant.NO_UK_ADDRESS
              ? {
                  movingDate: confirmModalState.data.movingDate,
                  address: undefined,
                }
              : {
                  address: confirmModalState.data.address,
                  movingDate: confirmModalState.data.movingDate,
                })}
            variant={confirmModalState.data.variant}
            onClose={closeConfirmModal}
            onConfirm={handleConfirmSubmission}
            onEdit={closeConfirmModal}
            isLoading={isApiLoading}
          />
        )}

        <Heading1 className={styles.title}>Moving home</Heading1>

        {apiError ? (
          <ErrorNotification
            title="Sorry, there’s a bit of a problem"
            id={'move-out-api-error'}
          >
            <P>
              We&apos;re working hard to fix it. But in the meantime, please try
              refreshing the page.
            </P>
          </ErrorNotification>
        ) : null}

        <div className={styles.description}>
          Tell us about your recent or upcoming move.
        </div>

        <form ref={formRef}>
          <MovingDateSection
            values={formValues.movingDate}
            errors={formErrors.movingDate}
            onValuesChange={valuesChangeHandler(
              'movingDate',
              formSubmissionState.submitted,
            )}
          />

          {addressProvider === 'gbg' && (
            <GbgAddressSection
              values={formValues}
              errors={formErrors.gbgAddress}
              onValuesChange={valuesChangeHandler('gbgAddress')}
              onErrorsChange={errorsChangeHandler('gbgAddress')}
              onGetQuote={({ isCot }) => {
                handleGbgAddressGetQuote({
                  isCot,
                  formSubmissionSource:
                    FormSubmissionSource.GBG_ADDRESS_GET_QUOTE,
                });
              }}
              onEndContract={submitHandler(
                FormSubmissionSource.GBG_ADDRESS_END_CONTRACT,
              )}
              onNoUkAddress={submitHandler(
                FormSubmissionSource.NO_UK_ADDRESS_NEXT,
              )}
              onCantFindAddress={() => {
                setAddressProvider('manual');
              }}
            />
          )}

          {addressProvider === 'manual' && (
            <ManualAddressSection
              values={formValues.manualAddress}
              errors={formErrors.manualAddress}
              onValuesChange={valuesChangeHandler(
                'manualAddress',
                formSubmissionState.submitted,
              )}
              onFindAddressFromPostcode={() => {
                setAddressProvider('gbg');
              }}
              onNext={submitHandler(FormSubmissionSource.MANUAL_ADDRESS_NEXT)}
            />
          )}
        </form>
      </Card>
      <Rewards />
    </>
  );
};
